import { Crumb, PageBreadcrumb } from "@/components/page-breadcrumb";
import {
  DetailAside,
  DetailAsideToggle,
  DetailColumns,
  DetailMain,
} from "@/components/detail-columns";
import Page403 from "@/components/zthd/403";
import Page404 from "@/components/zthd/404";
import Page500 from "@/components/zthd/500";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Chip } from "@/components/badge/chip";
import { Separator } from "@/components/ui/separator";
import { CornerLeftUpIcon, DownloadIcon, EllipsisIcon, ExternalLinkIcon, PencilIcon, RotateCwIcon, UserPlusIcon } from "lucide-react";
import { useUser, useUsersByIds } from "@/hooks/users";
import { useApp, useVisibleAppIds } from "@/hooks/apps";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { engagementDetailPath, pickWorkItem, prdDetailPath } from "@/lib/paths";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  useAuthStore,
  mutateRequest,
  optimisticPrd,
  optimisticPrds,
  optimisticEngagements,
  patchListItem,
  writeAtom,
  postFetcher,
  putFetcher,
  putFetcherKeyed,
  ROLE_LABELS,
  type RoleValue,
  type User,
} from "@/lib/swr";
import UserSelector from "@/components/user-selector";
import UsersSelector from "@/components/users-selector";
import { UserAvatar, UserChip } from "@/components/user-chip";
import { Property, PropertyList } from "@/components/detail/property";
import type { PickerUser } from "@/components/user-selector";
import type { Engagement, PersonalStatus, Prd, Version } from "@/types/domain";
import { AddButton } from "@/components/prds/add-button";
import { FollowButton } from "@/components/prds/follow-button";
import { PrdTagsEditor } from "@/components/prds/prd-tags";
import { PrdMembersEditor } from "@/components/prds/prd-members";
import { Section } from "@/components/section";
import {
  AddAppDropdown,
  AppSchedulingActions,
  ScheduleBoard,
} from "./schedule-board";
import { PrdContentSection } from "./prd-content";
import { AccessRequestsDialog, PrdNoAccess } from "./prd-access-request";
import { PrdThreadSection } from "./prd-thread";
import { PrdPullRequestsSection } from "./prd-pull-requests";
import { PrdTimelineSection } from "./prd-timeline";
import { ParentPickerDialog, ParentLine, PrdChildrenSection } from "./prd-parent";
import { DueEditor } from "./prd-due";
import { CopyShortId } from "@/components/prds/copy-short-id";
import { ExecStageChip } from "@/components/prds/exec-stage-chip";
import { AgentSessionChip } from "@/components/prds/agent-session-chip";
import { ExecMenu } from "@/components/prds/exec-menu";
import { ListLoading } from "@/components/list/status-text";
import { MyEngagementStatus, MyEngagementWork, personalStatusOf } from "@/components/engagements/my-completion";
import { STATUS_LABEL, STATUS_TONE, StatusIcon } from "@/components/engagements/completion-progress";
import { DATE_FULL } from "@/lib/date";
import SubmitBugs from "@/screens/Versions/SubmitBugs";
import { EngagementPriority } from "@/screens/Versions/Engagements/cells";

// Document editing and parent selection stay in the overflow menu.
// Personal Open / Done has its own primary control in the header.
const HeaderMenu = ({
  prd,
  editable,
  onEditDoc,
}: {
  prd: Prd;
  editable: boolean;
  onEditDoc: () => void;
}) => {
  const [parentOpen, setParentOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const pendingAccess = prd.pendingAccessRequests ?? 0;
  const hasEditableDoc = editable && (!!prd.content || !prd.docUrl);
  if (!editable) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="More actions"
            className="shrink-0 text-muted-foreground"
          >
            <EllipsisIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onCloseAutoFocus={(event) => { if (parentOpen || accessOpen) event.preventDefault(); }}>
          {hasEditableDoc && (
            <DropdownMenuItem onClick={onEditDoc}>
              <PencilIcon className="mr-1 h-3.5 w-3.5" />
              {prd.content ? "Edit" : "Write"}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setParentOpen(true)}>
            <CornerLeftUpIcon className="h-4 w-4" />
            {prd.type === "todo" ? "设置父项…" : "设置父需求…"}
          </DropdownMenuItem>
          {pendingAccess > 0 && (
            <DropdownMenuItem onSelect={() => setAccessOpen(true)}>
              <UserPlusIcon className="h-4 w-4" />
              访问申请 ({pendingAccess})
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {parentOpen && <ParentPickerDialog prd={prd} onOpenChange={setParentOpen} />}
      {accessOpen && <AccessRequestsDialog prd={prd} onOpenChange={setAccessOpen} />}
    </>
  );
};

// Title-row Sync for Feishu PRDs (next to Follow): first pull and re-syncs
// both live here — the document block below carries no sync control.
const HeaderSyncButton = ({ prd }: { prd: Prd }) => {
  const { trigger, isMutating } = useSWRMutation(
    `/prds/${prd.id}/sync-content`,
    postFetcher,
    { onSuccess: () => mutateRequest(`/prds/${prd.id}`) }
  );
  if (!prd.docUrl) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => trigger({})}
      disabled={isMutating}
    >
      {isMutating ? (
        <RotateCwIcon className="h-4 w-4 animate-spin" />
      ) : (
        <DownloadIcon className="h-4 w-4" />
      )}
      Sync
    </Button>
  );
};

// Bug against this PRD in its scheduled version (formerly in the version
// sheet's action row).
const EngagementSubmitBugs = ({ prd, engagement }: { prd: Prd; engagement: Engagement }) => {
  const { data: version } = useSWR<Version>([`/versions/${engagement.versionId}`]);
  if (!version) return null;
  return <SubmitBugs version={version} engagement={{ ...engagement, prd }} />;
};

// Fix a misfiled issue kind (PRD / Bug / Doc) via a compact aside select —
// type is just a hat, every issue converts freely.
const TYPE_LABEL = { prd: "PRD", bug: "Bug", doc: "Doc", todo: "Todo" } as const;

const TypeSelect = ({ prd, readOnly }: { prd: Prd; readOnly?: boolean }) => {
  const { trigger, isMutating } = useSWRMutation(
    `/prds/${prd.id}#type`,
    putFetcherKeyed<Prd>,
    {
      // The PUT echoes the PRD → detail. Lists still revalidate: a type flip
      // changes which filtered list the row belongs to (WED-249).
      onSuccess: (saved) => {
        writeAtom(`/prds/${prd.id}`, saved);
        mutateRequest("/prds");
      },
      onError: () => mutateRequest(`/prds/${prd.id}`),
    }
  );
  const change = (type: string) => {
    if (type === (prd.type ?? "prd")) return;
    const data = { type };
    trigger(data);
    optimisticPrd(prd.id, data);
    optimisticPrds(prd.id, data);
  };
  if (readOnly) return <Chip>{TYPE_LABEL[prd.type ?? "prd"]}</Chip>;
  return (
    <Select value={prd.type ?? "prd"} onValueChange={change}>
      <SelectTrigger size="sm"
        disabled={isMutating}
        className="w-24 px-2 text-xs"
        aria-label="Issue type"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="prd">PRD</SelectItem>
        <SelectItem value="bug">Bug</SelectItem>
        <SelectItem value="doc">Doc</SelectItem>
        {/* A todo must hang under a parent PRD (WED-148) */}
        <SelectItem value="todo" disabled={!prd.parentId}>
          Todo
        </SelectItem>
      </SelectContent>
    </Select>
  );
};

// Reassign the PRD owner (e.g. handing over a departed member's PRD).
const OwnerEditor = ({ prd }: { prd: Prd }) => {
  const { trigger, isMutating } = useSWRMutation(
    `/prds/${prd.id}#owner`,
    putFetcherKeyed<Prd>,
    {
      onSuccess: (saved) => {
        writeAtom(`/prds/${prd.id}`, saved);
        patchListItem("/prds", prd.id, { ownerId: saved.ownerId });
      },
    }
  );
  const owner = useUser(prd.ownerId);
  const onSelect = (user: PickerUser | null) => {
    if (user && user.id !== prd.ownerId) trigger({ ownerId: user.id });
  };
  return (
    <UserSelector
      selectedUser={owner}
      onContinue={onSelect}
      trigger={
        <AddButton
          title={isMutating ? "Updating…" : "Change owner"}
        />
      }
    />
  );
};

// Participants are member rows (WED-192); undefined = not a participant here.
// The viewer's own status comes from myCompletedAt/myStartedAt (updated optimistically).
const participantStatus = (e: Engagement, userId: string, viewerId?: string): PersonalStatus | undefined =>
  userId === viewerId && e.canComplete ? personalStatusOf(e)
    : e.completionParticipants?.find((p) => p.userId === userId)?.status;

// One person per row in the People aside: avatar, name, status (WED-189).
// `status` undefined = no personal status (owner without one).
const PersonRow = ({
  user,
  status,
  label,
}: {
  user: User;
  status?: PersonalStatus;
  /** Accessible name, e.g. "Alice — Done". */
  label: string;
}) => (
  <Link
    to={`/users/${user.id}/prds`}
    className="flex min-w-0 flex-1 items-center gap-2 hover:text-primary"
    aria-label={label}
    title={label}
  >
    <UserAvatar user={user} className="size-5" />
    <span className="min-w-0 truncate text-sm">{user.name}</span>
    {/* Functional roles (开发/测试/…) right after the name */}
    {user.roles?.map((role) => (
      <Chip key={role} tone="primary" className="px-1">
        {ROLE_LABELS[role as RoleValue] ?? role}
      </Chip>
    ))}
    {user.kind === "bot" && <Chip className="px-1">bot</Chip>}
    {status ? (
      <Chip tone={STATUS_TONE[status]} className="ml-auto inline-flex items-center gap-1">
        <StatusIcon status={status} className="size-3" />
        {STATUS_LABEL[status]}
      </Chip>
    ) : (
      <Chip className="ml-auto">Owner</Chip>
    )}
  </Link>
);

const EngagementPeople = ({
  engagement,
  ownerId,
}: {
  engagement: Engagement;
  ownerId?: string;
}) => {
  const { trigger, isMutating } = useSWRMutation(
    `/prds/${engagement.id}`,
    putFetcher<Prd>,
  );
  const members = useUsersByIds(engagement.memberIds);
  const viewerId = useAuthStore((state) => state.auth?.user?.id ?? state.auth?.id);
  // The roster mirrors the picker selection (WED-189): execution members only.
  // The PRD owner already has a dedicated row above — keep them in the picker
  // selection but don't repeat them here. A viewer who participates without
  // being a member (e.g. App owner) sees their own status in the header.
  const people = ownerId
    ? members.filter((member) => member.id !== ownerId)
    : members;
  const saveMembers = (selected: PickerUser[]) => {
    const memberIds = selected.map((member) => String(member.id));
    trigger(
      { executionMemberIds: memberIds },
      {
        // The PUT echoes the work item; the document-level detail (if that's
        // the page) only needs its engagements refreshed from it (WED-249).
        onSuccess: (saved) => {
          optimisticEngagements(engagement.id, { memberIds });
          writeAtom(`/prds/${engagement.id}`, saved);
          writeAtom<Prd>(`/prds/${engagement.prdId}`, (cur) => ({
            ...cur,
            engagements: saved.engagements ?? cur.engagements,
          }));
        },
      },
    );
  };

  if (people.length === 0 && !engagement.appId) return null;
  return (
    <div className="flex flex-col gap-2">
      {people.map((member) => {
        const status = participantStatus(engagement, member.id, viewerId) ?? "open";
        return (
          <PersonRow
            key={member.id}
            user={member}
            status={status}
            label={`${member.name} — ${STATUS_LABEL[status]}`}
          />
        );
      })}
      {engagement.appId && (
        <UsersSelector
          selectedUsers={members}
          onContinue={saveMembers}
          sourceUrl={`/apps/${engagement.appId}/members`}
          hint="Only members of this app are listed — ask the app owner to add anyone missing."
          trigger={<AddButton disabled={isMutating} title={isMutating ? "Updating…" : "添加成员"} />}
        />
      )}
    </div>
  );
};

// Route shell: /e/:engagementId (one App's work item) is the only PRD detail
// route (WED-275).
const Detail = () => {
  const { engagementId } = useParams();
  return <PrdDetailView engagementId={engagementId} />;
};

// Retired /prds/:id and /apps/:pid/prds/:id links (old bookmarks, Feishu
// history, document-only references): resolve the document and replace the
// URL with its work item — the :pid App's one when given, else one in an App
// the user can see.
// A document with no App relation still renders here as the document page.
export const PrdLegacyRedirect = () => {
  const { id, pid } = useParams();
  const location = useLocation();
  // Same key as PrdDetailView: SWR dedupes, and its loading / 403 / 404
  // states render while this resolves.
  const { data } = useSWR<Prd>(id ? [`/prds/${id}`] : null);
  // Wait for the visible App list so a doc linked to several Apps never lands
  // on a work item in an App this user can't see (that route would 403).
  const visibleAppIds = useVisibleAppIds();
  const target = visibleAppIds && pickWorkItem(data?.engagements, visibleAppIds, pid);
  if (target) {
    return (
      <Navigate
        to={{ pathname: engagementDetailPath(target.id), search: location.search, hash: location.hash }}
        replace
      />
    );
  }
  return <PrdDetailView id={id} pid={pid} />;
};

// The full PRD view, also embedded by the version page's sheet (WED-194):
// `embedded` drops the page breadcrumb, the sheet supplies its own header.
export const PrdDetailView = ({
  id,
  pid,
  engagementId,
  embedded,
}: {
  id?: string;
  pid?: string;
  engagementId?: string;
  embedded?: boolean;
}) => {
  const location = useLocation();
  // PRD service resolves the work identity and aggregates its content and App context.
  const prdId = engagementId ?? id;
  const { data: prd, isLoading, error } = useSWR<Prd>(
    prdId ? [`/prds/${prdId}`] : null,
    {
      // A Feishu PRD with an empty body means the creation-time sync is still
      // running (or the server-side lazy resync just kicked off on this open) —
      // poll until the content lands so the reader never stares at a blank doc.
      refreshInterval: (latest) => {
        if (latest?.docUrl && !latest.content) return 4000;
        // A teammate may finish in another session while an owner waits here.
        return latest?.engagements?.some(e => e.canRemind && !e.myCompletedAt) ? 10000 : 0;
      },
    }
  );

  const fromState = location.state as {
    breadcrumb?: Crumb[];
    appId?: string;
  } | null;
  // Router state supplies a return breadcrumb, but the URL owns the App
  // context so copied links and reloads keep the same personal work item.
  const contextAppId = engagementId
    ? prd?.appId
    : prd?.appId ?? pid ?? fromState?.appId;
  const app = useApp(contextAppId);
  const fromCrumbs = fromState?.breadcrumb ?? (contextAppId
    ? [{ label: app?.name ?? "App", to: `/apps/${contextAppId}/wish-pool` }]
    : undefined);

  if (isLoading) return <ListLoading>Loading PRD…</ListLoading>;
  const loadError = error;
  if (loadError?.status === 403) return prdId ? <PrdNoAccess id={prdId} /> : <Page403 />;
  if (loadError?.status === 404) return <Page404 />;
  if (loadError) return <Page500 />;
  if (!prd) return <Page404 />;

  return (
    <DetailBody
      key={`${prd.id}:${contextAppId ?? ""}`}
      prd={contextAppId ? {
        ...prd,
        shortId: prd.engagements?.find(e => e.appId === contextAppId)?.shortId ?? null,
        engagementId: prd.engagements?.find(e => e.appId === contextAppId)?.id,
      } : prd}
      fromCrumbs={fromCrumbs}
      contextAppId={contextAppId}
      embedded={embedded}
    />
  );
};

// Split so the doc-editing state hooks sit below the loading early-returns.
const DetailBody = ({
  prd,
  fromCrumbs,
  contextAppId,
  embedded,
}: {
  prd: Prd;
  fromCrumbs?: Crumb[];
  contextAppId?: string;
  embedded?: boolean;
}) => {
  // Edit/Write is triggered from the header ⋯ menu; the document block
  // below renders the editor when on.
  const [editingDoc, setEditingDoc] = useState(false);
  const [asideOpen, setAsideOpen] = useState(true);
  // People are id references on the PRD; resolve via the /users directory.
  const owner = useUser(prd.ownerId);
  const creator = useUser(prd.creatorId);
  const followers = useUsersByIds(prd.followerIds);
  const viewerId = useAuthStore((state) => state.auth?.user?.id ?? state.auth?.id);
  const contextEngagement = contextAppId
    ? prd.engagements?.find((e) => String(e.appId) === String(contextAppId))
    : undefined;
  const ownerStatusEngagements = contextEngagement
    ? [contextEngagement]
    : prd.engagements ?? [];
  // Across several Apps: Done only when every App is Done; any progress reads In Process.
  const ownerStatuses = owner
    ? ownerStatusEngagements.flatMap((e) => participantStatus(e, owner.id, viewerId) ?? [])
    : [];
  const ownerStatus: PersonalStatus | undefined = !ownerStatuses.length ? undefined
    : ownerStatuses.every((status) => status === "done") ? "done"
    : ownerStatuses.some((status) => status !== "open") ? "in_progress"
    : "open";
  const isBug = prd.type === "bug";
  const isDoc = prd.type === "doc";
  // Launching executes one App's work item: a document linked to several
  // Apps without App context has no single target (copy-only then).
  const execTarget = isDoc ? undefined : contextEngagement
    ?? (!contextAppId && prd.engagements?.length === 1 ? prd.engagements[0] : undefined);
  const hasSubPrds = prd.children?.some((child) => (child.type ?? "prd") === "prd");
  // Server-computed edit right (owner / creator / editor member). Every
  // write entry below hides on false; the server still enforces the rule.
  // Follow, thread posts and report docs stay open to viewers.
  const editable = prd.canEdit !== false;

  return (
    <div>
      {!embedded && (
        <PageBreadcrumb
          parent={fromCrumbs ?? { label: "PRDs", to: "/prds" }}
          current={prd.shortId ?? prd.docName}
        />
      )}

      {/* Header spans both columns so the action cluster has room
          (WED-196); body + thread left, people + properties right. */}
      <header>
        {/* Parent issue (WED-143) sits above the title as a breadcrumb-ish line */}
        <ParentLine prd={prd} />
        {/* Narrow screens: the action cluster wraps to its own row under
            the title instead of squeezing it out. */}
        <div className="flex flex-wrap items-start gap-2">
          {/* Star (icon only) leads the row; centered on the h-6 chips.
              autoFollowParam: ?follow=1 from Feishu cards. */}
          <FollowButton prd={prd} compact autoFollowParam className="mt-0.5" />
          {/* Priority belongs to this App's work item — only with App context. */}
          {contextEngagement && (
            <div className="mt-1 flex shrink-0">
              <EngagementPriority engagement={contextEngagement} readOnly={!editable} />
            </div>
          )}
          <CopyShortId shortId={prd.shortId} className="mt-1" />
          {isBug ? (
            <Chip tone="red" className="mt-1.5 shrink-0">
              Bug
            </Chip>
          ) : prd.type === "doc" ? (
            <Chip tone="cyan" className="mt-1.5 shrink-0">
              Doc
            </Chip>
          ) : prd.type === "todo" ? (
            <Chip tone="teal" className="mt-1.5 shrink-0">
              Todo
            </Chip>
          ) : null}
          {/* Title links straight to the Feishu doc. */}
          <h1 className={`min-w-0 text-2xl font-semibold leading-tight${contextEngagement?.myCompletedAt ? " text-muted-foreground line-through" : ""}`}>
            {prd.docUrl ? (
              <Link
                to={prd.docUrl}
                target="_blank"
                rel="noreferrer"
                title="Open in Feishu"
                className="hover:text-primary"
              >
                {prd.docName}
              </Link>
            ) : (
              prd.docName
            )}
          </h1>
          {prd.docUrl && (
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="mt-1 h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <Link
                to={prd.docUrl}
                target="_blank"
                rel="noreferrer"
                title="Open in Feishu"
                aria-label="Open in Feishu"
              >
                <ExternalLinkIcon className="h-4 w-4" />
              </Link>
            </Button>
          )}
          <AgentSessionChip prd={prd} className="mt-1.5" />
          <ExecStageChip prd={prd} className="mt-1.5" />
          <div className="ml-auto mt-0.5 flex w-full shrink-0 items-center justify-end gap-2 md:w-auto">
            {execTarget ? (
              <ExecMenu
                prdId={execTarget.id}
                docId={prd.id}
                appId={execTarget.appId ?? undefined}
                shortId={execTarget.shortId ?? undefined}
                engagement={execTarget}
              />
            ) : prd.shortId && (
              <ExecMenu prdId={prd.engagementId ?? prd.shortId} copyOnly />
            )}
            {editable && <HeaderSyncButton prd={prd} />}
            {contextEngagement?.versionId && (prd.type ?? "prd") === "prd" && (
              <EngagementSubmitBugs prd={prd} engagement={contextEngagement} />
            )}
            {contextEngagement && (
              <MyEngagementStatus engagement={contextEngagement} prdId={prd.id} issueType={prd.type} />
            )}
            <HeaderMenu
              prd={prd}
              editable={editable}
              onEditDoc={() => setEditingDoc(true)}
            />
            <DetailAsideToggle open={asideOpen} onOpenChange={setAsideOpen} />
          </div>
        </div>

        <PrdChildrenSection prd={prd} types={["prd"]} className="mt-1" />

        <Separator className={hasSubPrds ? "mb-6 mt-2" : "my-6"} />
      </header>

      <DetailColumns>
        {/* Main */}
        <DetailMain>
          <section aria-label="Labels" className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            {/* Any viewer may label the PRD (server: tagIds is visible-level). */}
            <PrdTagsEditor prd={prd} />
          </section>

          <PrdPullRequestsSection prd={prd} />

          <PrdContentSection
            prd={prd}
            editing={editingDoc}
            onEditingChange={setEditingDoc}
          />

          <PrdChildrenSection prd={prd} types={["bug", "todo", "doc"]} />

          {!contextAppId && <MyEngagementWork prd={prd} />}

          {/* Reports render inside the thread stream (owner 2026-08-18). */}
          <PrdThreadSection prd={prd} appId={contextAppId} />
        </DetailMain>

        {/* Owner and execution members stay visible here with their personal
            completion state; tags stay under the title separator. */}
        <DetailAside open={asideOpen}>
            <section aria-label="People" className="mb-4 space-y-3">
              <div className="flex items-center gap-2">
                {owner && (
                  <PersonRow
                    user={owner}
                    status={ownerStatus}
                    label={`${owner.name}: ${ownerStatus ? STATUS_LABEL[ownerStatus] : "Owner"}`}
                  />
                )}
                {editable && <OwnerEditor prd={prd} />}
              </div>
              {(contextEngagement ? [contextEngagement] : prd.engagements ?? []).map((e) => (
                <EngagementPeople
                  key={e.id}
                  engagement={e}
                  ownerId={owner?.id}
                />
              ))}
            </section>
            {/* Timeline sits above the Apps & versions board (owner 2026-10-22). */}
            <PrdTimelineSection prdId={prd.id} />
            {/* Scheduling and App links stay with the Apps & versions board. */}
            <Section
              title="Apps & versions"
              action={<AddAppDropdown prd={prd} compact />}
            >
              {contextAppId && <AppSchedulingActions prd={prd} appId={contextAppId} />}
              <ScheduleBoard prd={prd} readOnly={!editable} />
            </Section>

            <PropertyList>
              {/* Author stays even after the owner is reassigned. */}
              {creator && (
                <Property label="Author">
                  <Link
                    to={`/users/${creator.id}/prds`}
                    className="flex min-w-0 items-center hover:text-primary"
                  >
                    <UserChip user={creator} />
                  </Link>
                </Property>
              )}

              <Property label="Created">
                {prd.createdAt
                  ? format(new Date(prd.createdAt), DATE_FULL)
                  : "—"}
              </Property>

              {/* Creation source: absent = created by hand in the UI (shown
                  as "web"); set by API/agent callers (e.g. "wed-version-prd"). */}
              <Property label="Source">
                <Chip tone={prd.source ? "cyan" : undefined}>
                  {prd.source ?? "web"}
                </Chip>
              </Property>

              {/* Misfiled-kind fix: PRD / Bug / Doc, freely convertible */}
              <Property label="Type">
                <TypeSelect prd={prd} readOnly={!editable} />
              </Property>

              {/* Due date: todos always show it (Inbox sorts overdue first);
                  other kinds only once one is set */}
              {(prd.type === "todo" || prd.dueAt) && (
                <Property label="Due">
                  <DueEditor prd={prd} readOnly={!editable} />
                </Property>
              )}

              {/* Bug provenance: where it was found (scheduling lives in the
                  Apps & versions board like any issue). */}
              {isBug && prd.sourceVersion && (
                <Property label="Source version">
                  <Link
                    to={`/apps/${prd.sourceVersion.pid}/versions/${prd.sourceVersion.id}`}
                    className="hover:underline"
                  >
                    <Chip tone="blue">
                      {prd.sourceVersion.versionName}
                      {prd.sourceVersion.versionCode &&
                        `(${prd.sourceVersion.versionCode})`}
                    </Chip>
                  </Link>
                </Property>
              )}
              {isBug && prd.sourcePrd && (
                <Property label="Source PRD">
                  <Link
                    to={prdDetailPath(prd.sourcePrd.id, prd.sourceEngagementId ?? undefined)}
                    className="hover:underline"
                  >
                    <Chip tone="purple" className="max-w-44 truncate">
                      {prd.sourcePrd.docName}
                    </Chip>
                  </Link>
                </Property>
              )}
              {/* Degraded reporter byline when the creator isn't registered */}
              {isBug && !creator && prd.creatorName && (
                <Property label="Reporter">{prd.creatorName}</Property>
              )}

              {followers.length > 0 && (
                <Property label="Followers">
                  <div className="flex items-center">
                    {followers.map((u) => (
                      <UserAvatar
                        key={u.id}
                        user={u}
                        className="-ml-1.5 border-2 border-background first:ml-0"
                      />
                    ))}
                  </div>
                </Property>
              )}
            </PropertyList>

            {/* Doc viewer list (read-only grants); owner, creator and linked-App
                members edit by role and are never listed. */}
            {(editable || (prd.memberIds?.length ?? 0) > 0) && (
              <Section title="可查看">
                <PrdMembersEditor prd={prd} readOnly={!editable} />
              </Section>
            )}
        </DetailAside>
      </DetailColumns>
    </div>
  );
};

export default Detail;
